import { RefObject, useEffect, useState } from 'react'

interface UseVirtualScrollProps {
    containerRef: RefObject<HTMLDivElement>
    positions: number[]
    suggestionsLength: number
    containerHeight: number
    buffer?: number
}

/**
 * @description Custom hook to handle virtual scrolling for a list of suggestions.
 * This hook calculates the visible range of items based on the scroll position
 * and updates the state accordingly.
 * It uses a buffer to preload items around the visible range
 * to ensure smooth scrolling experience.
 * This hook listens to scroll events on the container
 * and adjusts the visible range dynamically.
 * It also handles window resize events to recalculate the visible range
 * when the container height changes.
 * This is useful for optimizing performance
 * when rendering large lists of items
 * by only rendering the items that are currently visible in the viewport.
 * @param {UseVirtualScrollProps} props - The properties for the virtual scroll hook.
 * @returns { start: number, end: number } - The visible range of items in the list.
 */
export const useVirtualScroll = ({
    containerRef,
    positions,
    suggestionsLength,
    containerHeight,
    buffer = 5,
}: UseVirtualScrollProps) => {
    const [visibleRange, setVisibleRange] = useState<{
        start: number
        end: number
    }>({ start: 0, end: 20 })

    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        let ticking = false
        const calculateVisibleRange = () => {
            const scrollTop = container.scrollTop
            let start = 0
            let end = suggestionsLength

            for (let i = 0; i < positions.length; i++) {
                if ((positions[i] || 0) + (positions[i] || 0) > scrollTop) {
                    start = Math.max(0, i - buffer)
                    break
                }
            }
            for (let i = start; i < positions.length; i++) {
                if ((positions[i] || 0) > scrollTop + containerHeight) {
                    end = Math.min(suggestionsLength, i + buffer)
                    break
                }
            }
            if (visibleRange.start !== start || visibleRange.end !== end) {
                setVisibleRange({ start, end })
            }
            ticking = false
        }

        const handleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(calculateVisibleRange)
                ticking = true
            }
        }

        container.addEventListener('scroll', handleScroll, { passive: true })
        window.addEventListener('resize', handleScroll, { passive: true })
        calculateVisibleRange()

        return () => {
            container.removeEventListener('scroll', handleScroll)
            window.removeEventListener('resize', handleScroll)
        }
    }, [positions, suggestionsLength, containerHeight, buffer])

    return visibleRange
}
